import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, ArrowLeft, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      
      try {
        await axios.get(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (err) {
        setTokenValid(false);
        setError(err.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password?token=${token}&new_password=${encodeURIComponent(password)}`);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_cf3e6afe-39f0-4f7d-b7fa-374d76bd5dd8/artifacts/dqtzduwl_PH%20Tennis%20Top%20View.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/80" />
        
        <div className="relative z-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_ops-central-26/artifacts/w6yec208_players-health-lockup-color%20copy%20%284%29.png" 
            alt="Players Health" 
            className="h-10"
          />
        </div>
        
        <div className="text-white relative z-10">
          <h1 className="text-4xl font-bold mb-4 leading-tight tracking-tight drop-shadow-lg">
            Marketing Command Center
          </h1>
          <p className="text-slate-200 text-lg leading-relaxed drop-shadow-md">
            Create a new password for your account.
          </p>
        </div>

        <div className="text-slate-300 text-sm relative z-10">
          &copy; 2026 Players Health. All rights reserved.
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
              success ? 'bg-green-100' : !tokenValid && !verifying ? 'bg-red-100' : 'bg-blue-100'
            }`}>
              {success ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : !tokenValid && !verifying ? (
                <XCircle className="w-6 h-6 text-red-600" />
              ) : (
                <Lock className="w-6 h-6 text-blue-600" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {success ? 'Password Reset!' : !tokenValid ? 'Invalid Link' : 'Reset Password'}
            </CardTitle>
            <CardDescription>
              {success 
                ? "Your password has been successfully reset" 
                : !tokenValid 
                  ? "This reset link is invalid or has expired"
                  : "Enter your new password below"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center py-6">
                <p className="text-slate-600 mb-6">
                  You can now log in with your new password.
                </p>
                <Link to="/login">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Go to Login
                  </Button>
                </Link>
              </div>
            ) : !tokenValid ? (
              <div className="text-center py-6">
                <p className="text-slate-600 mb-6">
                  {error || 'The password reset link is invalid or has expired. Please request a new one.'}
                </p>
                <div className="space-y-3">
                  <Link to="/forgot-password">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700">
                      Request New Reset Link
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="ghost" className="w-full gap-2">
                      <ArrowLeft className="w-4 h-4" />
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">Minimum 6 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>

                <div className="text-center pt-4">
                  <Link 
                    to="/login" 
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
